/**
 * InterviewResultsPage
 * Shows interview results in a single view matching the Word document format.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  UserIcon,
  BriefcaseIcon,
  StarIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { aiInterviewsApi, apiV2 } from '../../services/api'
import type { AIInterview } from '../../types/interview'

// Status color mapping
const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  questions_generated: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  audio_uploaded: 'bg-purple-100 text-purple-800',
  transcribed: 'bg-indigo-100 text-indigo-800',
  analyzed: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
}

export default function InterviewResultsPage() {
  const { interviewId } = useParams<{ interviewId: string }>()
  const navigate = useNavigate()

  // State
  const [interview, setInterview] = useState<AIInterview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  // Ref to prevent concurrent fetches
  const isFetchingRef = useRef(false)
  const initialFetchDoneRef = useRef(false)

  // Fetch interview details
  const fetchInterview = useCallback(async (showLoading = true) => {
    if (!interviewId || isFetchingRef.current) return

    isFetchingRef.current = true
    try {
      if (showLoading) setIsLoading(true)
      const response = await aiInterviewsApi.getDetails(interviewId)
      setInterview(response.data as AIInterview)
      setError(null)
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to load interview results'
      setError(message)
    } finally {
      if (showLoading) setIsLoading(false)
      isFetchingRef.current = false
    }
  }, [interviewId])

  // Initial fetch - only once
  useEffect(() => {
    if (!initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true
      fetchInterview(true)
    }
  }, [fetchInterview])

  // Auto-refresh while the backend queue is actively processing this interview.
  // audio_uploaded → Celery transcribing
  // transcribed    → Celery running AI analysis
  // Both are transitional; polling stops as soon as status leaves these states.
  useEffect(() => {
    if (!interview) return

    const isTransitional =
      interview.status === 'audio_uploaded' ||
      interview.status === 'transcribed'

    if (isTransitional) {
      const interval = setInterval(() => fetchInterview(false), 5000)
      return () => clearInterval(interval)
    }
  }, [interview?.status, fetchInterview])

  // Format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview results...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !interview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Results</h2>
          <p className="text-gray-600 mb-6">{error || 'Interview not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Processing state - automated processing in progress
  if (interview.status === 'audio_uploaded') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="relative mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mx-auto"></div>
            <ChartBarIcon className="h-8 w-8 text-purple-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Processing Interview</h2>
          <p className="text-gray-600 mb-4">
            Your interview recording is being analyzed automatically.
          </p>
          <p className="text-sm text-gray-500">
            This may take a few minutes. The page will update automatically when complete.
          </p>
        </div>
      </div>
    )
  }

  // Extract analysis data - handle multiple response formats
  const overallAnalysis = interview.overall_analysis as any
  const questionAnalysesRaw = interview.question_analyses as any[] | undefined

  // Get data from the new workflow format or legacy format
  // New workflow: overall_analysis.interview_report.question_wise_evaluation
  // Current format: question_analyses at root level with question_score_out_of_10
  // Legacy format: question_analyses with score field
  const interviewReport = overallAnalysis?.interview_report || {}

  // Overall score - check multiple locations
  const overallScoreValue = interviewReport.overall_score?.score_out_of_100
    || overallAnalysis?.overall_score
    || interview.overall_score
  const overallScoreRationale = interviewReport.overall_score?.score_rationale

  // Final recommendation
  const finalRecommendation = interviewReport.final_recommendation || {}
  const recommendationDecision = finalRecommendation.decision || overallAnalysis?.recommendation || interview.recommendation
  const recommendationJustification = finalRecommendation.justification

  // Summary
  const overallSummary = interviewReport.overall_summary || overallAnalysis?.summary

  // Strengths and gaps/concerns
  const strengths = interviewReport.strengths_identified || overallAnalysis?.key_strengths || overallAnalysis?.strengths || []
  const gaps = interviewReport.gaps_identified || overallAnalysis?.key_concerns || overallAnalysis?.concerns || []

  // Question evaluations - check multiple formats
  // Format 1: interview_report.question_wise_evaluation (new workflow)
  // Format 2: question_analyses at root with question_score_out_of_10 (current format from API)
  // Format 3: question_analyses with score field (legacy)
  const questionEvaluations = interviewReport.question_wise_evaluation || []

  // Use questionAnalysesRaw if it has the new format fields (question_score_out_of_10)
  const hasNewFormatQuestions = questionAnalysesRaw && questionAnalysesRaw.length > 0 &&
    (questionAnalysesRaw[0].question_score_out_of_10 !== undefined || questionAnalysesRaw[0].candidate_response_analysis)

  // If we have new format questions at root level, use them as questionEvaluations
  const effectiveQuestionEvaluations = questionEvaluations.length > 0
    ? questionEvaluations
    : (hasNewFormatQuestions ? questionAnalysesRaw : [])

  // Legacy question analyses (only if not using new format)
  const legacyQuestionAnalyses = !hasNewFormatQuestions ? questionAnalysesRaw : []

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">AI Interview Report</h1>
                <p className="text-sm text-gray-500">Session: {interview.session_id}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[interview.status]}`}>
                {interview.status.replace(/_/g, ' ')}
              </span>
              {interview.status === 'analyzed' && (
                <button
                  onClick={async () => {
                    if (isDownloading) return // Prevent double-click
                    setIsDownloading(true)
                    try {
                      const response = await apiV2.get(
                        `/ai-interviews/${interview.id}/download-report`,
                        { responseType: 'blob' }
                      )
                      const blob = response.data
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${interview.candidate_name?.replace(/\s+/g, '_')}_AI_Interview_Report_${interview.session_id}.pdf`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      window.URL.revokeObjectURL(url)
                    } catch (error) {
                      console.error('Download failed:', error)
                      alert(error instanceof Error ? error.message : 'Failed to download report')
                    } finally {
                      setIsDownloading(false)
                    }
                  }}
                  disabled={isDownloading}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isDownloading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Download Report
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Single scrollable view */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Section 1: Interview Details */}
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
            Interview Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <UserIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Candidate Name</p>
                <p className="font-medium text-gray-900">{interview.candidate_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <BriefcaseIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Position</p>
                <p className="font-medium text-gray-900">{interview.job_title || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Interview Date</p>
                <p className="font-medium text-gray-900">{formatDate(interview.audio_uploaded_at)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <ClockIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                <p className="font-medium text-gray-900">
                  {interview.audio_duration_seconds
                    ? `${Math.floor(interview.audio_duration_seconds / 60)}m ${interview.audio_duration_seconds % 60}s`
                    : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <StarIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Matching Score</p>
                <p className="font-medium text-gray-900">
                  {interview.matching_score ? `${(interview.matching_score * 100).toFixed(1)}%` : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">AI Interview Score</p>
                <p className="font-medium text-gray-900">
                  {overallScoreValue !== undefined && overallScoreValue !== null
                    ? `${Math.round(overallScoreValue)}/100`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Interview Recording (Video) */}
        {interview.video_blob_name && interview.video_sas_url && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="h-5 w-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Interview Recording
            </h2>
            <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-lg">
              <video
                controls
                controlsList="nodownload"
                className="w-full h-auto"
                style={{ maxHeight: '600px' }}
              >
                <source src={interview.video_sas_url} type="video/webm" />
                Your browser does not support video playback.
              </video>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4" />
                <span>Recorded on {formatDate(interview.video_uploaded_at)}</span>
              </div>
              {interview.video_file_size && (
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="h-4 w-4" />
                  <span>{(interview.video_file_size / (1024 * 1024)).toFixed(1)} MB</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Section 3: Overall Score */}
        {overallScoreValue !== undefined && overallScoreValue !== null && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Score</h2>
            <div className="flex items-center space-x-4 mb-4">
              <div className={`text-4xl font-bold ${
                overallScoreValue >= 70 ? 'text-green-600' :
                overallScoreValue >= 50 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {overallScoreValue}/100
              </div>
            </div>
            {overallScoreRationale && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Score Rationale:</p>
                <p className="text-gray-600">{overallScoreRationale}</p>
              </div>
            )}
          </section>
        )}

        {/* Section 4: Final Recommendation */}
        {recommendationDecision && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Final Recommendation</h2>
            <div className="mb-4">
              {(() => {
                const isRecommended = String(recommendationDecision).toLowerCase() === 'yes' ||
                  String(recommendationDecision).toLowerCase().includes('hire')
                return (
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg text-lg font-semibold ${
                    isRecommended
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {isRecommended ? (
                      <>
                        <CheckCircleIcon className="h-6 w-6 mr-2" />
                        RECOMMENDED FOR HIRE
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-6 w-6 mr-2" />
                        NOT RECOMMENDED
                      </>
                    )}
                  </div>
                )
              })()}
            </div>
            {recommendationJustification && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Justification:</p>
                <p className="text-gray-600">{recommendationJustification}</p>
              </div>
            )}
          </section>
        )}

        {/* Section 4: Overall Summary */}
        {overallSummary && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Summary</h2>
            <p className="text-gray-700 leading-relaxed">{overallSummary}</p>
          </section>
        )}

        {/* Section 5: Strengths Identified */}
        {strengths.length > 0 && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600" />
              Strengths Identified
            </h2>
            <ul className="space-y-2">
              {strengths.map((strength: string, index: number) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-green-500 mt-1">•</span>
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Section 6: Areas for Improvement */}
        {gaps.length > 0 && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ExclamationCircleIcon className="h-5 w-5 mr-2 text-orange-500" />
              Areas for Improvement
            </h2>
            <ul className="space-y-2">
              {gaps.map((gap: string, index: number) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span className="text-gray-700">{gap}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Section 7: Question-by-Question Evaluation (New Format) */}
        {effectiveQuestionEvaluations.length > 0 && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Question-by-Question Evaluation</h2>
            <div className="space-y-8">
              {effectiveQuestionEvaluations.map((qa: any, index: number) => (
                <div key={index} className="border-l-4 border-blue-500 bg-gray-50 rounded-r-lg">
                  {/* Question Header */}
                  <div className="bg-blue-50 px-5 py-4 border-b border-blue-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-gray-900">
                          Question {qa.question_id || index + 1}
                        </h3>
                        <p className="text-gray-700 mt-1">{qa.primary_question || 'N/A'}</p>
                      </div>
                      {qa.question_score_out_of_10 !== undefined && (
                        <div className={`ml-4 flex flex-col items-center px-4 py-2 rounded-lg ${
                          qa.question_score_out_of_10 >= 7 ? 'bg-green-100' :
                          qa.question_score_out_of_10 >= 4 ? 'bg-yellow-100' :
                          'bg-red-100'
                        }`}>
                          <span className={`text-2xl font-bold ${
                            qa.question_score_out_of_10 >= 7 ? 'text-green-700' :
                            qa.question_score_out_of_10 >= 4 ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {qa.question_score_out_of_10}
                          </span>
                          <span className="text-xs text-gray-600">out of 10</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="px-5 py-4 space-y-4">
                    {/* Conversation Summary */}
                    {qa.conversation_summary && (
                      <div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Conversation Summary:</p>
                        <p className="text-gray-700 bg-white p-3 rounded border border-gray-200">{qa.conversation_summary}</p>
                      </div>
                    )}

                    {/* Response Analysis */}
                    {qa.candidate_response_analysis && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                          Response Analysis
                        </h4>

                        {/* Ideal Answer Expectation */}
                        {qa.candidate_response_analysis.ideal_answer_expectation && (
                          <div className="mb-4">
                            <p className="text-sm font-bold text-gray-800 mb-1">Ideal Answer Expectation:</p>
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {qa.candidate_response_analysis.ideal_answer_expectation}
                            </p>
                          </div>
                        )}

                        {/* Expected Keywords */}
                        {qa.candidate_response_analysis.expected_keywords?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-bold text-gray-800 mb-2">Expected Keywords:</p>
                            <div className="flex flex-wrap gap-2">
                              {qa.candidate_response_analysis.expected_keywords.map((keyword: string, kidx: number) => (
                                <span key={kidx} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Evaluation Criteria Table */}
                        {qa.candidate_response_analysis.guide_sentence_evaluation?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-bold text-gray-800 mb-2">Evaluation Criteria:</p>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-800 border-b border-gray-200">Criteria</th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-800 border-b border-gray-200">Status</th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-800 border-b border-gray-200">Evidence</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {qa.candidate_response_analysis.guide_sentence_evaluation.map((guide: any, gidx: number) => (
                                    <tr key={gidx} className={gidx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">
                                        {guide.guide_sentence || '-'}
                                      </td>
                                      <td className="px-4 py-3 border-b border-gray-100">
                                        <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${
                                          guide.status === 'met' || guide.status === 'fully_met'
                                            ? 'bg-green-100 text-green-800'
                                            : guide.status === 'partially_met'
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : 'bg-red-100 text-red-800'
                                        }`}>
                                          {(guide.status || '').replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-600 border-b border-gray-100">
                                        {guide.evidence || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* AI Summary */}
                        {qa.candidate_response_analysis.ai_summary && (
                          <div>
                            <p className="text-sm font-bold text-gray-800 mb-1">AI Analysis:</p>
                            <p className="text-gray-700 text-sm leading-relaxed bg-blue-50 p-3 rounded border border-blue-100">
                              {qa.candidate_response_analysis.ai_summary}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Legacy Question Analysis (if no new format) */}
        {effectiveQuestionEvaluations.length === 0 && legacyQuestionAnalyses && legacyQuestionAnalyses.length > 0 && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Question-by-Question Analysis</h2>
            <div className="space-y-8">
              {legacyQuestionAnalyses.map((qa: any, index: number) => (
                <div key={index} className="border-l-4 border-blue-500 bg-gray-50 rounded-r-lg">
                  {/* Question Header */}
                  <div className="bg-blue-50 px-5 py-4 border-b border-blue-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-gray-900">
                          Question {qa.question_number || index + 1}
                        </h3>
                        <p className="text-gray-700 mt-1">{qa.question_text}</p>
                      </div>
                      {qa.score !== undefined && (
                        <div className={`ml-4 flex flex-col items-center px-4 py-2 rounded-lg ${
                          qa.score >= 7 ? 'bg-green-100' :
                          qa.score >= 4 ? 'bg-yellow-100' :
                          'bg-red-100'
                        }`}>
                          <span className={`text-2xl font-bold ${
                            qa.score >= 7 ? 'text-green-700' :
                            qa.score >= 4 ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {qa.score}
                          </span>
                          <span className="text-xs text-gray-600">out of 10</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="px-5 py-4 space-y-4">
                    {/* Response Summary */}
                    {qa.response_summary && (
                      <div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Response Summary:</p>
                        <p className="text-gray-700 bg-white p-3 rounded border border-gray-200">{qa.response_summary}</p>
                      </div>
                    )}

                    {/* Candidate Response */}
                    {qa.candidate_response && (
                      <div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Candidate Response:</p>
                        <p className="text-gray-700 bg-white p-3 rounded border border-gray-200">{qa.candidate_response}</p>
                      </div>
                    )}

                    {/* Analysis */}
                    {qa.analysis && (
                      <div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Analysis:</p>
                        <p className="text-gray-700 bg-white p-3 rounded border border-gray-200">{qa.analysis}</p>
                      </div>
                    )}

                    {/* Strengths and Weaknesses */}
                    {((qa.strengths && qa.strengths.length > 0) || (qa.weaknesses && qa.weaknesses.length > 0) || (qa.areas_for_improvement && qa.areas_for_improvement.length > 0)) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {qa.strengths && qa.strengths.length > 0 && (
                          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                            <p className="text-sm font-bold text-green-800 mb-2">Strengths:</p>
                            <ul className="space-y-1">
                              {qa.strengths.map((s: string, i: number) => (
                                <li key={i} className="text-sm text-green-700 flex items-start">
                                  <CheckCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(qa.weaknesses?.length > 0 || qa.areas_for_improvement?.length > 0) && (
                          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                            <p className="text-sm font-bold text-orange-800 mb-2">Areas for Improvement:</p>
                            <ul className="space-y-1">
                              {(qa.weaknesses || qa.areas_for_improvement || []).map((w: string, i: number) => (
                                <li key={i} className="text-sm text-orange-700 flex items-start">
                                  <ExclamationCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                                  {w}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Skills Demonstrated */}
                    {qa.skills_demonstrated && qa.skills_demonstrated.length > 0 && (
                      <div>
                        <p className="text-sm font-bold text-gray-800 mb-2">Skills Demonstrated:</p>
                        <div className="flex flex-wrap gap-2">
                          {qa.skills_demonstrated.map((skill: string, sidx: number) => (
                            <span key={sidx} className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* No Data Available Message */}
        {!overallSummary && strengths.length === 0 && gaps.length === 0 &&
         effectiveQuestionEvaluations.length === 0 && (!legacyQuestionAnalyses || legacyQuestionAnalyses.length === 0) && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Analysis Not Available</h3>
            <p className="text-gray-500">
              Interview analysis data is not yet available. Please check back after the interview has been processed.
            </p>
          </section>
        )}

      </div>
    </div>
  )
}
